import { useEffect, useMemo, useState } from "react"
import { HStack, Separator, Spinner, Stack } from "@chakra-ui/react"
import { Button, Section, Text } from "@/components"
import { useAuth, useViewer } from "@/contexts"
import { Deal, DealStep, PortalRole } from "@/types"
import { getCashFlowsByDealId, CashFlow } from "@/api/payments"
import { useClientInfo } from "@/hooks"
import {
  DealSummarySection,
  ForeignWireInformationFormComponent,
  PaymentLedgerSection,
  UsPlaidWireInformationComponent,
} from "./components"

// ── Mock deals for Plaid testing (shown only when ESO person is impersonating) ──

const MOCK_DEAL: Deal = {
  id: 0,
  company: { name: "Test Company Inc." },
  offers: [
    {
      id: 0,
      dealId: 0,
      offerType: "primary",
      isStaged: 1,
      exerciseCost: 350,           // $3.50
      desiredLiquidity: 150,       // $1.50
      allocationAgreement: "",
      pp: 100,
      ppSplit: 100,
      lp: 0,
      isPreferentialReturn: 0,
      optionsDocFee: 0,
      upFrontFee: 0,
      hypotheticalSharePrice: 1000,
      isStagedInPortal: true,
      isAccepted: true,
      isIncentive: 0,
      incentivePp: null,
      incentivePpSplit: null,
      incentiveLp: null,
      incentiveDeadline: null,
      offerGrants: [],
      offerNumber: 1,
      createdAt: "",
      updatedAt: "",
      ltv: 0,
    },
  ],
  dealManager: { id: 0, name: "Dm Test", email: "dm@test.com", phoneNumber: "" },
  inceptionDate: "",
  expirationDate: "",
  updatedAt: "",
  portalStage: DealStep.SEND_WIRE_INFO,
  grants: [],
}

const MOCK_DEAL_LIQUIDITY: Deal = {
  ...MOCK_DEAL,
  id: 1,
  company: { name: "Test Company Inc. 2" },
  offers: [
    {
      ...MOCK_DEAL.offers[0],
      id: 1,
      exerciseCost: null,
      desiredLiquidity: 150,       // $1.50
      isAccepted: true,
    },
  ],
}

const MOCK_DEAL_IDS = new Set([0, 1])

type PaymentMethod = "us" | "foreign"

interface Props {
  isLoading: boolean
  deals: Deal[]
}

export const PaymentCenterComponent = ({ deals, isLoading }: Props) => {
  const { user } = useAuth()
  const { isImpersonating, clientId } = useViewer()
  const { data: clientInfo } = useClientInfo(clientId)
  const [currentDeal, setCurrentDeal] = useState<Deal | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null)
  const [cashFlows, setCashFlows] = useState<CashFlow[]>([])
  const [cashFlowsLoading, setCashFlowsLoading] = useState(false)

  const effectiveDeals = useMemo(() => {
    if (isImpersonating) {
      return [MOCK_DEAL, MOCK_DEAL_LIQUIDITY, ...(deals || [])]
    }
    return deals
  }, [deals, isImpersonating])

  useEffect(() => {
    if (effectiveDeals && effectiveDeals.length > 0) {
      setCurrentDeal(effectiveDeals[0])
    }
  }, [effectiveDeals])

  useEffect(() => {
    if (!currentDeal) return
    if (MOCK_DEAL_IDS.has(currentDeal.id)) {
      setCashFlows([])
      setCashFlowsLoading(false)
      return
    }
    setCashFlowsLoading(true)
    getCashFlowsByDealId(String(currentDeal.id))
      .then(data => { setCashFlows(data) })
      .catch(() => { setCashFlows([]) })
      .finally(() => { setCashFlowsLoading(false) })
  }, [currentDeal])

  const clientUser = user?.userType === PortalRole.CLIENT ? user : undefined

  const [signDbFirst, ...signDbLastParts] = (clientInfo?.legalName ?? "").split(" ")
  const signDbLast = signDbLastParts.join(" ")

  const defaultClientData = {
    firstName: clientUser?.firstName || signDbFirst || "",
    lastName: clientUser?.lastName || signDbLast || "",
    email: clientUser?.email || clientInfo?.email || "",
    companyName: currentDeal?.company?.name ?? "",
    addressLine1: clientInfo?.address || undefined,
    city: clientInfo?.city || undefined,
    state: clientInfo?.state || undefined,
    zip: clientInfo?.zip || undefined,
    country: clientInfo?.country || undefined,
  }

  // ── Determine payment status from deal + cash flows ──
  const acceptedOffer = currentDeal?.offers?.find(o => o.isAccepted) ?? currentDeal?.offers?.[0]
  const exerciseCostDollars = acceptedOffer?.exerciseCost != null ? acceptedOffer.exerciseCost / 100 : null
  const liquidityDollars = acceptedOffer?.desiredLiquidity != null ? acceptedOffer.desiredLiquidity / 100 : null

  const totalPaid = cashFlows.reduce((sum, cf) => sum + Math.abs(cf.amount), 0)
  const exercisePaid = exerciseCostDollars != null && exerciseCostDollars > 0 && totalPaid >= exerciseCostDollars
  const bothPaid = exercisePaid && liquidityDollars != null && liquidityDollars > 0 && totalPaid >= (exerciseCostDollars! + liquidityDollars)

  let paymentAmount = 0
  let paymentLabel = ""

  if (bothPaid) {
    paymentAmount = 0
    paymentLabel = "No Pending Payments"
  } else if (exercisePaid) {
    paymentAmount = liquidityDollars ?? 0
    paymentLabel = "Additional Liquidity Payment (Pending)"
  } else {
    paymentAmount = exerciseCostDollars ?? liquidityDollars ?? 0
    paymentLabel = exerciseCostDollars != null ? "Exercise Payment (Pending)" : "Additional Liquidity Payment (Pending)"
  }

  return (
    <Section>
      <Text fontSize="2xl" fontWeight="bold" fontFamily="body">
        Payment Center
      </Text>
      {
        isLoading ? (
          <HStack w="100%" justifyContent="center" alignItems="center" p={10}>
            <Spinner />
          </HStack>
        ) : (
          <>
            {effectiveDeals && effectiveDeals.length > 1 && (
              <HStack mt={5} gap={2}>
                {effectiveDeals.map((deal, index) => (
                  <Button
                    key={index}
                    variant={deal.id === currentDeal?.id ? "solid" : "outline"}
                    onClick={() => {
                      setCurrentDeal(deal)
                      setSelectedMethod(null)
                    }}
                  >
                    {deal.company.name}
                  </Button>
                ))}
              </HStack>
            )}

            {currentDeal && (
              <Stack gap={6} mt={5}>
                <DealSummarySection currentDeal={currentDeal} />

                <Separator />

                <PaymentLedgerSection cashFlows={cashFlows} isLoading={cashFlowsLoading} />

                <Separator />

                {/* Payment method selection */}
                <Text fontSize="lg" fontWeight="semibold">
                  Select Payment Method
                </Text>
                <HStack gap={3}>
                  <Button
                    variant={selectedMethod === "us" ? "solid" : "outline"}
                    onClick={() => setSelectedMethod("us")}
                    flex={1}
                  >
                    US Bank Account
                  </Button>
                  <Button
                    variant={selectedMethod === "foreign" ? "solid" : "outline"}
                    onClick={() => setSelectedMethod("foreign")}
                    flex={1}
                  >
                    Foreign Bank Account
                  </Button>
                </HStack>

                {/* Render selected payment form */}
                {selectedMethod === "us" && (
                  <>
                    <Separator />
                    <Text fontSize="lg" fontWeight="semibold">
                      US Bank Account Payment Center
                    </Text>
                    <UsPlaidWireInformationComponent
                      defaultClientData={defaultClientData}
                      paymentAmount={paymentAmount}
                      paymentLabel={paymentLabel}
                    />
                  </>
                )}

                {selectedMethod === "foreign" && (
                  <>
                    <Separator />
                    <Text fontSize="lg" fontWeight="semibold">
                      Foreign Bank Account Payment Center
                    </Text>
                    <ForeignWireInformationFormComponent
                      defaultClientData={defaultClientData}
                      paymentAmount={paymentAmount}
                      paymentLabel={paymentLabel}
                    />
                  </>
                )}
              </Stack>
            )}
          </>
        )
      }
    </Section>
  )
}
